<template>
  <div class="contentStyle">
    <div class="headerStyle">
      <div class="titleStyle font-weight-medium">
        {{ $t('aquassistBilling.billingRegister.title') }}
      </div>
      <v-btn
        color="secondary"
        @click="onClickExportCsv"
      >
        {{ $t('csv.exportCsv') }}
        <v-icon right>
          mdi-cloud-download
        </v-icon>
      </v-btn>
    </div>
    <SearchWithFilters
      :saveFilter="true"
      :searchPlaceholder="$t('aquassistBilling.billingRegister.searchBy')"
      :filters="filters"
      :filterName="filterName"
      isDateMonth
      :showTextField="showManagementBillingRegister"
      @onApplyFilters="handleApplyFilters"
      @onSearch="handleSearch"
    />
    <VTableRemote
      ref="tableRemoteRef"
      class="billingCustomTable"
      :items="allCurentBilling"
      hideDefaultFooter
      :headers="tableHeaders"
      :pagination="{}"
      isCustomIcon
      :isSearchable="false"
      :centerColumns="['actions']"
      :notSortableColumns="['actions']"
      :loading="isFetching"
      :isInfinitScroll="true"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td class="text-start">
            <div
              class="d-flex align-center mLeft"
              @click="showData(item.serviceProviderId)"
            >
              <v-icon
                v-if="show[item.serviceProviderId]"
                large
                color="primary"
              >
                mdi-menu-down
              </v-icon>
              <v-icon
                v-else
                large
                color="primary"
              >
                mdi-menu-right
              </v-icon>
            </div>
          </td>
          <td class="text-start">
            {{ item.serviceProviderName }}
          </td>
          <td class="text-start">
            <span
              class="textDecoration"
              @click="onClickDetails('userDetails', item.serviceProviderId)"
            >
              {{ item.users.nbrUserActive }} <span :class="{redColor: item.users.nbrUserInactive}">({{ item.users.nbrUserInactive }})</span>
            </span>
          </td>
          <td class="text-start">
            <span
              class="textDecoration"
              @click="onClickDetails('siteDetails', item.serviceProviderId)"
            >
              {{ item.sites.nbrSiteActive }} <span :class="{redColor: item.sites.nbrSiteInactive}">({{ item.sites.nbrSiteInactive }})</span>
            </span>
          </td>
          <td class="text-start">
            {{ item.features.nbrFeatureActive }} <span :class="{redColor: item.features.nbrFeatureInactive}">
              ({{ item.features.nbrFeatureInactive }})</span>
          </td>
          <td class="text-start">
            ${{ item.total }}
          </td>
          <td class="d-flex justify-center">
            <v-menu offsetY>
              <template #activator="{ on }">
                <div class="d-flex justify-end actionPadding">
                  <v-btn
                    color="primary"
                    xSmall
                    fab
                    rounded
                    v-on="on"
                  >
                    <v-icon>
                      mdi-dots-vertical
                    </v-icon>
                  </v-btn>
                </div>
              </template>

              <v-list>
                <!-- Edit -->
                <ListItem
                  icon="mdi-pencil"
                  :title="$t('forms.labels.edit')"
                />
              </v-list>
            </v-menu>
          </td>
        </tr>
        <td
          v-if="show[item.serviceProviderId]"
          class="AquassistManagePadding"
          :colspan="tableHeaders.length"
        >
          <CurrentBillingManagementDetails
            :items="item"
            :openAllSubItems="showManagementBillingRegister"
          />
        </td>
      </template>
    </VTableRemote>
  </div>
</template>

<script>
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import VTableRemote from '@/components/shared/content-views/VTableRemote.vue';
import CurrentBillingManagementDetails from '@/views/admin/aquassistBilling/billingRegisterComponent/curentBillingSummaryDetails/CurrentBillingManagementDetails.vue';
import Vue from 'vue';
import CurrentBillingExport
from '@/views/admin/aquassistBilling/billingRegisterComponent/curentBillingSummaryDetails/CurrentBillingExport.vue';
import UserDetails from '@/views/admin/aquassistBilling/billingRegisterComponent/curentBillingSummaryDetails/UserDetails.vue';
import SiteDetails from '@/views/admin/aquassistBilling/billingRegisterComponent/curentBillingSummaryDetails/SiteDetails.vue';
import { mapGetters } from 'vuex';
import roleConstants from '@/utilities/enums/rolesTypes';
import moment from 'moment/moment';

export default {
    name: 'CurrentBillingSummary',
    components: {
        CurrentBillingManagementDetails,
        VTableRemote,
        SearchWithFilters,
    },
    props: {
        notManagement: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isFetching: false,
            show: {},
            filterName: 'CurrentBillingSummary',
            allCurentBilling: [],
            queryParams: {
                startDate: new Date().toISOString(),
            },
            filters: [
                {
                    key: 'date',
                    label: 'aquassistBilling.billingRegister.labels.billingPeriod',
                    type: 'date',
                    value: moment().date(3).startOf('day').toISOString(),
                    dateKeys: ['startDate'],
                    isRange: false,
                },
            ],
        };
    },
    computed: {
        ...mapGetters([
            'hasRole',
            'teamID',
            'superAdmin',
        ]),
        showManagementBillingRegister() {
            return (this.hasRole(roleConstants.ADMINISTRATOR)
                || this.hasRole(roleConstants.MANAGER)
                || this.hasRole(roleConstants.FINANCIALADMINISTRATOR)
                || this.superAdmin) && this.notManagement;
        },
        tableHeaders() {
            return [
                {
                    text: '',
                    value: 'icon',
                    sortable: false,
                    width: 20,
                },
                {
                    text: this.$t('aquassistBilling.billingRegister.labels.serviceProvider'),
                    value: 'serviceProvider',
                },
                {
                    text: this.$t('aquassistBilling.billingRegister.labels.users'),
                    value: 'users',
                },
                {
                    text: this.$t('aquassistBilling.billingRegister.labels.sites'),
                    value: 'sites',
                },
                {
                    text: this.$t('aquassistBilling.billingRegister.labels.features'),
                    value: 'features',
                },
                {
                    text: `${this.$t('aquassistBilling.billingRegister.labels.total')} ($)`,
                    value: 'total',
                },
                {
                    text: this.$t('table.actions'),
                    value: 'actions',
                },
            ];
        },
    },
    async created() {
        await this.getCurrentBillingSummary();
    },
    methods: {
        async getCurrentBillingSummary() {
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined({
                ...this.queryParams,
                team: this.showManagementBillingRegister ? null : this.teamID,
            }, true);
            const { data } = await this.$rq.getCurrentBilling(cleanQueryParams);
            const newData = data.map(item => ({
                ...item,
                total: (this.getTotal(item.users.usersDetails, 'users')
                    + this.getTotal(item.sites.sitesDetails, 'sites')
                    + this.getTotal(item.features.featuresDetails, 'features')),
            }));
            if (this.showManagementBillingRegister) {
                this.allCurentBilling = newData;
            } else {
                this.allCurentBilling = newData;
                this.allCurentBilling.forEach(s => {
                    this.show = { ...this.show, [s.serviceProviderId]: true };
                });
            }
        },
        handleSearch(search) {
            this.queryParams.search = search;
            this.getCurrentBillingSummary();
        },
        handleApplyFilters(filterParams = {}) {
            if (filterParams?.reset) {
                this.queryParams = {
                    startDate: new Date().toISOString(),
                };
            } else {
                this.queryParams = {
                    ...this.queryParams,
                    ...filterParams,
                    search: this.queryParams?.search || '',
                };
            }
            this.getCurrentBillingSummary();
        },
        getTotal(item, type) {
            if (type === 'users') {
                return item.map(user => user.price * user.active).reduce((acc, el) => acc + el, 0);
            }
            if (type === 'sites') {
                return item.map(site => site.price * site.active).reduce((acc, el) => acc + el, 0);
            }
            if (type === 'features') {
                return item.map(feature => feature.price * feature.active).reduce((acc, el) => acc + el, 0);
            }
            return 0;
        },
        onClickExportCsv() {
            this.$root.$emit('showModal', {
                title: this.$t('aquassistBilling.billingRegister.labels.exportCsv'),
                payload: { oneServiceProvider: this.showManagementBillingRegister },
                component: Vue.component('CurrentBillingExport', CurrentBillingExport),
                onConfirm: async requestPayload => {
                    const finalRequestPayload = { ...requestPayload, selectedDate: this.queryParams.startDate };
                    await this.exportCsv(finalRequestPayload);
                },
                controlledValidation: true,
                isForm: true,
                width: 800,
            });
        },
        async exportCsv(requestPayload) {
            const { data } = await this.$rq.getCurrentBilling(requestPayload);
            const detail = data.sitesDetails ? 'sites' : 'users';
            const headers = this.createCsv(data.sitesDetails ? data.sitesDetails : data.usersDetails);
            const csvTitle = `billing_summary_${detail}_${moment(requestPayload.selectedDate).format('YYYY-MM')}.csv`;
            this.$utils.exportSimpleCSV(headers, data.sitesDetails ? data.sitesDetails : data.usersDetails, csvTitle, 'billingRegister', detail);
        },
        createCsv(data) {
            const capitalize = v => `${v[0].toUpperCase()}${v.slice(1)}`;
            return Object.keys(data[0]).map(capitalize);
        },
        onClickDetails(detail, teamId) {
            const component = detail === 'userDetails' ? UserDetails : SiteDetails;
            this.$root.$emit('showModal', {
                title: this.$t(`aquassistBilling.billingRegister.labels.${detail}`),
                payload: { teamId },
                component: Vue.component(detail, component),
                showConfirmButton: false,
            });
        },
        showData(item) {
            this.show = {
                ...this.show,
                [item]: !this.show[item],
            };
        },
    },

};

</script>

<style scoped lang="scss">
@import "@/assets/styles/massBalanceStyle.scss";
.headerStyle{
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.contentStyle{
    padding: 1rem 2rem !important;
}
.billingCustomTable{
    ::v-deep table{
        padding:0 5px 0 0;
    }
    ::v-deep .blocHeader {
       height: 0;
    }
    .redColor{
        color: red;
    }
}
.AquassistManagePadding{
  padding-right: 0;
}
.textDecoration{
  text-decoration: underline;
}
</style>
